import { IconContentCopy2 } from '@doltech/icons/IconContentCopy2';
import { IconContentEditV2 } from '@doltech/icons/IconContentEditV2';
import { IconContentUnlink } from '@doltech/icons/IconContentUnlink';
import * as React from 'react';
import { useUpdateEffect } from 'react-use';
import { useSlate } from 'slate-react';
import styled from 'styled-components';
import { useClipboard } from 'use-clipboard-copy';

import { getNodes } from '../../../../../slatePlugins/common/queries/getNodes';
import { Button } from '../../../../ButtonV2/ButtonV2';
import { colorsV2 } from '../../../../colors-v2';
import { Box } from '../../../../Common/BoxV2';
import { Typography } from '../../../../DesignSystemV2/Typography';
import { Link } from '../../../../Link';
import { shadows } from '../../../../shadows';
import { showToast } from '../../../../Toast';
import { DolBalloonToolbar } from '../../../toolbars/BalloonToolbar';
import { usePreventHiddenToolbar } from '../../../toolbars/prevent-toolbar-hidden/PreventToolbarHiddenContext';
import { HyperLinkPopoverOnCollapsed } from '../components/HyperLinkPopoverOnCollapsed';
import { ELEMENT_HYPER_LINK } from '../defaults';
import { unwrapLink } from '../transform/insertLinkAtSelection';
import { LinkElement } from '../types';
import { DolTooltip } from '../../../../Tooltip/DolTooltip';
import { Tooltip } from 'antd';

const MAX_WIDTH = 512;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  .action {
    &:not(:last-child) {
      margin-right: 8px;
    }
  }
`;

const Layout = styled.div`
  display: flex;
  align-items: center;

  .link-truncate {
    width: 350px;
  }

  .layout-section {
    &:not(:last-child) {
      margin-right: 16px;
    }

    &.link {
      color: ${colorsV2.blue100};

      &:hover {
        color: ${colorsV2.blue60};
      }
    }
  }
`;

const EditHyperLinkAction = () => {
  const { setPreventToolbarHidden } = usePreventHiddenToolbar();

  return (
    <HyperLinkPopoverOnCollapsed
      onApplyLink={() => setPreventToolbarHidden(false)}
      onPopoverClose={() => setPreventToolbarHidden(false)}
      onPopoverFocus={() => setPreventToolbarHidden(true)}
    >
      <Tooltip title="Edit link" placement="bottom">
        <Button.Icon className="action" type="secondary" variant="transparent" shape="rounded-rect">
          <IconContentEditV2 />
        </Button.Icon>
      </Tooltip>
    </HyperLinkPopoverOnCollapsed>
  );
};

const balloonToolbarStyles = {
  root: {
    background: 'transparent',
    padding: 0,
    borderRadius: 8,
    border: 'none',
    zIndex: 9999,
  },
};

export const ToolbarHyperLinkContextAction = () => {
  const editor = useSlate();
  const [url, setUrl] = React.useState('');

  useUpdateEffect(() => {
    if (editor.selection) {
      const [linkItem]: LinkElement[] = Array.from(
        getNodes(editor, {
          match: { type: ELEMENT_HYPER_LINK } as any,
        }),
        ([node]) => node as LinkElement
      );
      setUrl(linkItem?.url);
    }
  });

  const { copy } = useClipboard({
    onSuccess() {
      showToast({
        icon: 'success',
        title: 'Link copied to clipboard',
      });
    },
    onError() {
      showToast({
        icon: 'error',
        title: 'Fail to copy to clipboard',
      });
    },
  });

  return (
    <DolBalloonToolbar.OnCollapsed
      type={ELEMENT_HYPER_LINK}
      direction="bottom"
      theme="light"
      styles={balloonToolbarStyles}
    >
      <Box p={16} borderRadius={12} bg="white100" boxShadow={shadows.zIndex2} maxWidth={MAX_WIDTH}>
        <Layout>
          <DolTooltip title="Open link in new tab" placement="bottom">
            <Link href={url} target="_blank">
              <Typography.Text
                className="layout-section link link-truncate"
                variant="medium/16"
                ellipsis={{ tooltip: url }}
              >
                {url}
              </Typography.Text>
            </Link>
          </DolTooltip>

          <ActionContainer className="layout-section">
            <DolTooltip title="Copy link to clipboard" placement="bottom">
              <Button.Icon
                className="action"
                type="secondary"
                variant="dark"
                shape="rounded-rect"
                onMouseDown={(event) => {
                  event.preventDefault();
                  copy(url);
                }}
              >
                <IconContentCopy2 />
              </Button.Icon>
            </DolTooltip>

            <EditHyperLinkAction />

            <DolTooltip title="Unlink" placement="bottom">
              <Button.Icon
                onMouseDown={() => unwrapLink(editor)}
                className="action"
                type="secondary"
                variant="transparent"
                shape="rounded-rect"
              >
                <IconContentUnlink />
              </Button.Icon>
            </DolTooltip>
          </ActionContainer>
        </Layout>
      </Box>
    </DolBalloonToolbar.OnCollapsed>
  );
};
